import {
  Attribute,
  ChangeDetectorRef,
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  Injector,
  Type,
  ViewContainerRef,
} from '@angular/core';
import {
  ActivatedRoute,
  ChildrenOutletContexts,
  Data,
  NavigationEnd,
  Router,
  RouterOutlet,
  UrlTree,
} from '@angular/router';

import { from, Observable, of } from 'rxjs';
import { filter, first } from 'rxjs/operators';

import { DialogContainerComponent } from '../dialog/dialog-container/dialog-container.component';
import { DialogRef } from '../dialog/dialog-ref';
import { DialogService } from '../dialogs/dialogs.service';

class OutletInjector implements Injector {
  constructor(
    private route: ActivatedRoute,
    private childContexts: ChildrenOutletContexts,
    private parent: Injector,
  ) {}

  public get(token: any, notFoundValue?: any): any {
    if (token === ActivatedRoute) {
      return this.route;
    }

    if (token === ChildrenOutletContexts) {
      return this.childContexts;
    }

    return this.parent.get(token, notFoundValue);
  }
}

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'dialog-outlet',
})
export class DialogOutletDirective {
  private dialogRef: DialogRef | null;
  private activated: ComponentRef<DialogContainerComponent> | null;

  constructor(
    private parentContexts: ChildrenOutletContexts,
    private resolver: ComponentFactoryResolver,
    private cdRef: ChangeDetectorRef,
    private vcRef: ViewContainerRef,
    private router: Router,
    private dialogService: DialogService,
    private _activatedRoute: ActivatedRoute,
    // eslint-disable-next-line @angular-eslint/no-attribute-decorator
    @Attribute('name') private name: string,
  ) {
    if (!name) {
      throw new Error('Dialog Outlet requires name attribute.');
    }

    parentContexts.onChildOutletCreated(this.name, <RouterOutlet>(<unknown>this));
  }

  public ngOnDestroy(): void {
    this.parentContexts.onChildOutletDestroyed(this.name);
  }

  public ngOnInit(): void {
    if (!this.activated) {
      // If the outlet was not instantiated at the time the route got activated we need to populate
      // the outlet when it is initialized (ie inside a NgIf)
      const context = this.parentContexts.getContext(this.name);
      if (context && context.route) {
        if (context.attachRef) {
          // `attachRef` is populated when there is an existing component to mount
          this.attach(context.attachRef, context.route);
        } else {
          // otherwise the component defined in the configuration is created
          this.activateWith(context.route, context.resolver || null);
        }
      }
    }
  }

  public get isActivated(): boolean {
    return !!this.activated;
  }

  public get component(): {} {
    if (!this.activated) throw new Error('Outlet is not activated');
    return this.activated.instance;
  }

  public get activatedRoute(): ActivatedRoute {
    if (!this.activated) throw new Error('Outlet is not activated');
    return <ActivatedRoute>this._activatedRoute;
  }

  public get activatedRouteData(): Data {
    if (this._activatedRoute) {
      return this._activatedRoute.snapshot.data;
    }
    return {};
  }

  /**
   * Called when the `RouteReuseStrategy` instructs to detach the subtree
   */
  public detach(): ComponentRef<any> {
    if (!this.activated) throw new Error('Outlet is not activated');
    this.vcRef.detach();
    const cmp = this.activated;
    this.activated = null;
    return cmp;
  }

  /**
   * Called when the `RouteReuseStrategy` instructs to re-attach a previously detached subtree
   */
  public attach(ref: ComponentRef<any>, activatedRoute: ActivatedRoute) {
    throw new Error('Attach from "RouteReuseStrategy" not implemented in dialog-outlet');
  }

  public deactivate(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
      this.dialogRef = null;
    }
  }

  public activateWith(activatedRoute: ActivatedRoute, resolver: ComponentFactoryResolver | null) {
    if (this.dialogRef) this.dialogRef.close();

    this.openDialog(activatedRoute);

    this.router.events
      .pipe(
        filter(evt => evt instanceof NavigationEnd),
        first(),
      )
      .subscribe(() => {
        if (this.dialogRef) {
          if (this.dialogRef.componentInstance.setDialogData) {
            this.dialogRef.componentInstance.setDialogData(
              activatedRoute.snapshot.data.dialogData,
              true,
            );
          } else {
            this.dialogRef.containerInstance.detectChanges();
          }
        }
      });
  }

  private openDialog(activatedRoute: ActivatedRoute) {
    const { snapshot } = activatedRoute;

    const childContexts = this.parentContexts.getOrCreateContext(this.name).children;

    this.dialogRef = this.dialogService.openCustom(
      <Type<any>>activatedRoute.component,
      {
        ...(snapshot.data.options || {}),
        injector: new OutletInjector(activatedRoute, childContexts, this.vcRef.injector),
        closeGuard: () => this.syncLocationOnDialogClose(),
      },
      snapshot.data.dialogData,
      this.vcRef,
    );
  }

  private syncLocationOnDialogClose(): Observable<boolean> {
    const nav = this.router.getCurrentNavigation();

    if (!nav) {
      const url = this.router.parseUrl(this.router.url);
      this.removeOutletFromUrlTree(url);
      const urlWithoutOutlet = url.toString();

      return from(this.router.navigateByUrl(urlWithoutOutlet));
    }

    return of(true);
  }

  private removeOutletFromUrlTree(tree: UrlTree) {
    delete tree.root.children[this.name];
    return tree;
  }
}
